import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { PCNode } from '../../../MineModel/Nodes';

interface DeleteNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  onCancel: () => void;
  selectedNodes: PCNode[];
  deleteErrors: string[];
  isDeleting: boolean;
}

const DeleteNodeDialog: React.FC<DeleteNodeDialogProps> = ({
  isOpen,
  onClose,
  onDelete,
  onCancel,
  selectedNodes,
  deleteErrors,
  isDeleting,
}) => {
  const count = selectedNodes.length;
  const nodeLabel = count !== 1 ? i18n._(' Nodes') : i18n._(' Node');
  const hasErrors = deleteErrors.length > 0;

  const renderContent = () => {
    if (isDeleting) {
      return (
        <Box
          sx={{
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body1">
            <Trans id="Deleting nodes...">Deleting nodes...</Trans>
          </Typography>
        </Box>
      );
    }

    if (hasErrors) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
            <Trans id="Selected nodes cannot be deleted:">Selected nodes cannot be deleted:</Trans>
          </Typography>
          {deleteErrors.map((error) => (
            <Typography key={1} color="error" variant="body2" gutterBottom>
              -
              {error}
            </Typography>
          ))}
        </Box>
      );
    }

    return (
      <Typography variant="body1" gutterBottom>
        <Trans id="Are you sure you want to delete">
          Are you sure you want to delete
        </Trans>
        {` ${count} ${nodeLabel}?`}
        <Trans id=" This action cannot be undone.">This action cannot be undone.</Trans>
      </Typography>
    );
  };

  return (
    <DraggableDialog
      isOpen={isOpen}
      title={hasErrors ? i18n._('Cannot Delete Nodes') : i18n._('Delete Nodes')}
      position={{ x: window.innerWidth / 2 - 250, y: window.innerHeight / 2 - 150 }}
      dialogSize={{ width: 500, height: 250 }}
      onClose={isDeleting ? () => { } : onClose}
      onDrag={() => { }}
      onResizeStart={() => { }}
    >
      <Box sx={{ p: 2 }}>
        {renderContent()}

        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={hasErrors ? onClose : onCancel}
            color="secondary"
            disabled={isDeleting}
          >
            {hasErrors ? <Trans id="Close">Close</Trans> : <Trans id="Cancel">Cancel</Trans>}
          </Button>
          {!hasErrors && (
            <Button
              variant="contained"
              onClick={onDelete}
              color="error"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isDeleting ? (
                <Trans id="Deleting...">Deleting...</Trans>
              ) : (
                <>
                  <Trans id="Delete">Delete</Trans>
                  {` ${count} ${nodeLabel} `}
                </>
              )}
            </Button>
          )}
        </Box>
      </Box>
    </DraggableDialog>
  );
};

export default DeleteNodeDialog;
